import { collectLeaves, keyCovers, readPath, violates, writePath } from './contract.js';

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const mentions = (text, word) => new RegExp(`(^|[^a-z0-9_])${escapeRe(word.toLowerCase())}([^a-z0-9_]|$)`).test(text);

export const parseChange = (raw) => {
    const text = raw.toLowerCase();
    const times = /(?:^|\s)(?:x|×)\s?(\d+(?:\.\d+)?)\b/.exec(text) || /\b(\d+(?:\.\d+)?)\s?(?:x|×)(?:\s|$)/.exec(text);
    if (times) return { op: 'multiply', value: Number(times[1]) };
    if (/\bdouble\b/.test(text)) return { op: 'multiply', value: 2 };
    if (/\btriple\b/.test(text)) return { op: 'multiply', value: 3 };
    if (/\bhalve\b|\bhalf\b/.test(text)) return { op: 'multiply', value: 0.5 };
    const pct = /(\d+(?:\.\d+)?)\s?%/.exec(text);
    if (pct) {
        const down = /\b(reduce|cut|lower|decrease|cheaper|drop|discount|less)\b/.test(text);
        const p = Number(pct[1]) / 100;
        return { op: 'multiply', value: Math.round((down ? 1 - p : 1 + p) * 1000) / 1000 };
    }
    const flag = /\b(?:to|=)\s*(true|false|on|off)\b/.exec(text);
    if (flag) return { op: 'set', value: flag[1] === 'true' || flag[1] === 'on' };
    const set = /\b(?:to|=|at)\s*(-?\d+(?:\.\d+)?)\b/.exec(text);
    if (set) return { op: 'set', value: Number(set[1]) };
    const add = /(?:\+|\bplus\b|\badd\b)\s*(\d+(?:\.\d+)?)/.exec(text);
    if (add) return { op: 'add', value: Number(add[1]) };
    if (/\b(disable|turn off|switch off)\b/.test(text)) return { op: 'set', value: false };
    if (/\b(enable|turn on|switch on)\b/.test(text)) return { op: 'set', value: true };
    return null;
};

const findKey = (text, files) => {
    let best = null;
    files.forEach((f) => {
        collectLeaves(f.content).forEach((leaf) => {
            const last = leaf.path.split('.').pop();
            const hit = mentions(text, leaf.path) ? leaf.path.length + 100 : (mentions(text, last) ? last.length : 0);
            if (hit && (!best || hit > best.score)) best = { file: f, path: leaf.path, score: hit };
        });
    });
    return best;
};

const nextValue = (current, op, value) => {
    if (op === 'set') return value;
    const n = Number(current);
    if (!Number.isFinite(n) || typeof current === 'boolean') return current;
    const out = op === 'multiply' ? n * value : n + value;
    return Math.round(out * 100) / 100;
};

export const applyAction = (content, action) => {
    const leaves = collectLeaves(content).filter((l) => keyCovers(action.key, l.path));
    if (leaves.length === 0) {
        writePath(content, action.key, action.op === 'set' ? action.value : nextValue(readPath(content, action.key), action.op, action.value));
        return;
    }
    leaves.forEach((l) => writePath(content, l.path, nextValue(l.value, action.op || 'set', action.value)));
};

export const describeAction = (a) => {
    if (a.type === 'AUDIENCE') return `Audience: ${a.segment}`;
    if (a.type === 'WINDOW') return `Live for ${a.hours}h`;
    if (a.op === 'multiply') return `${a.key} ×${a.value}`;
    if (a.op === 'add') return `${a.key} ${Number(a.value) >= 0 ? '+' : ''}${a.value}`;
    return `${a.key} → ${JSON.stringify(a.value)}`;
};

const usedSnippets = (text, snippets) => snippets.filter((s) => (s.title || '').toLowerCase().split(/[^a-z0-9]+/)
    .some((w) => w.length >= 5 && text.includes(w)));

const display = (v) => (typeof v === 'string' ? `"${v}"` : String(v));

const violation = (r) => {
    if (r.op === 'MIN') return `is below the minimum ${r.numValue}`;
    if (r.op === 'MAX') return `is above the maximum ${r.numValue}`;
    if (r.op === 'ONE_OF') return `must be one of [${r.strValues.join(', ')}]`;
    return 'must not be empty';
};

const outcome = (status, extra = {}) => ({
    status, actions: [], changes: [], questions: [], note: null, sources: [], citedKnowledgeIds: [], ruleBreaks: [], outputs: [],
    dependsOn: [], ...extra,
});

const ask = (text) => ({ text, options: [] });

// Playground stand-in for backend processing: same statuses, actions and rule breaks as TaskResultDTO.
export const processTask = (task, ctx) => {
    if (task.actor === 'MANUAL') return outcome('MANUAL');

    const text = `${task.title} ${task.description || ''}`.toLowerCase();
    const cited = usedSnippets(text, ctx.snippets || []).map((k) => k.id);
    const linked = (task.links || []).filter((l) => l.targetType === 'CONFIG').map((l) => l.targetId);
    const files = linked.length ? ctx.configs.filter((c) => linked.includes(c.id)) : ctx.configs;

    if (/\?\s*$/.test(task.title.trim())) {
        const hit = (ctx.snippets || []).find((s) => s.id === cited[0]);
        return outcome('COMPLETED', { note: hit ? `${hit.title}: ${hit.body}` : 'Nothing this event knows answers that yet.', citedKnowledgeIds: cited });
    }

    const actions = [];
    const ruleBreaks = [];
    const key = findKey(text, files);
    const change = parseChange(text);
    if (key && change) {
        const breaks = collectLeaves(key.file.content).filter((l) => keyCovers(key.path, l.path)).flatMap((l) => {
            const next = nextValue(l.value, change.op, change.value);
            return (ctx.rules || []).filter((r) => r.configFileId === key.file.id && r.keyPath === l.path && violates(r, next))
                .map((r) => `${l.path} ${display(next)} ${violation(r)}`);
        });
        if (breaks.length) ruleBreaks.push(...breaks);
        else actions.push({ type: 'SET_VALUE', fileId: key.file.id, key: key.path, op: change.op.toUpperCase(), value: change.value });
    }

    (ctx.segments || []).forEach((s) => { if (s.name && mentions(text, s.name)) actions.push({ type: 'AUDIENCE', segmentId: s.id }); });
    const win = /\bfor\s+(\d+)\s*(hours?|h|days?|d)\b/.exec(text);
    if (win) actions.push({ type: 'WINDOW', hours: /^d/.test(win[2]) ? Number(win[1]) * 24 : Number(win[1]) });

    if (key && !change) {
        return outcome('NEEDS_INPUT', { questions: [ask(`How should ${key.path} change? Choose one: "x2", "+20%" or "to 100"`)], citedKnowledgeIds: cited });
    }
    if (!key && change) {
        const sample = collectLeaves(files[0]?.content).slice(0, 3).map((l) => `"${l.path}"`).join(', ');
        return outcome('NEEDS_INPUT', {
            questions: [ask(files.length ? `Which value should change? e.g. ${sample}` : 'Which config should change? Link one to this event first.')],
            citedKnowledgeIds: cited,
        });
    }
    if (actions.length || ruleBreaks.length) return outcome('COMPLETED', { actions, ruleBreaks, citedKnowledgeIds: cited });
    return outcome('NOT_ACTIONABLE', { note: 'it names no config value, audience or window.' });
};

const clone = (v) => JSON.parse(JSON.stringify(v ?? {}));

export const mockTestRun = (list, ctx) => {
    const working = Object.fromEntries((ctx.configs || []).map((c) => [c.id, clone(c.content)]));
    const now = new Date().toISOString();
    const results = [...list.items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).map((item) => {
        const configs = (ctx.configs || []).map((c) => ({ ...c, content: working[c.id] }));
        const result = processTask({ ...item, actor: item.actor || 'AI' }, { ...ctx, configs });
        result.actions.filter((a) => a.type === 'SET_VALUE').forEach((a) => {
            const file = configs.find((c) => c.id === a.fileId);
            const leaves = collectLeaves(working[a.fileId]).filter((l) => keyCovers(a.key, l.path));
            applyAction(working[a.fileId], { key: a.key, op: a.op.toLowerCase(), value: a.value });
            leaves.forEach((l) => result.changes.push({
                fileId: a.fileId, fileName: file?.name, key: l.path, before: l.value, after: readPath(working[a.fileId], l.path),
            }));
        });
        const blocksStaging = result.status === 'NEEDS_INPUT' || result.ruleBreaks.length > 0;
        return { itemId: item.id, title: item.title, actor: item.actor || 'AI', result: { ...result, blocksStaging, processedAt: now } };
    });
    return {
        taskListId: list.id,
        liveEventId: list.beatId,
        forOccurrenceStart: ctx.nextMs ? new Date(ctx.nextMs).toISOString() : null,
        forOccurrenceEnd: null,
        processingState: 'IDLE',
        processingStartedAt: now,
        ranAt: now,
        failureReason: null,
        results,
    };
};

export const proposeConfigEdit = (message, text) => {
    let doc;
    try {
        doc = JSON.parse(text);
    } catch {
        return { reply: 'The editor text isn’t valid JSON, so there is nothing to edit yet.', proposedContent: null };
    }
    const lower = message.toLowerCase();
    const key = findKey(lower, [{ id: 'doc', name: 'doc', content: doc }]);
    const change = parseChange(lower);
    if (!key || !change) {
        return {
            reply: key
                ? `How should ${key.path} change? Say "x2", "+20%" or "to 100".`
                : 'Which value should change? Name a key in this file, e.g. "set enabled to false".',
            proposedContent: null,
        };
    }
    const before = readPath(doc, key.path);
    applyAction(doc, { key: key.path, ...change });
    return {
        reply: `Changed ${key.path} from ${JSON.stringify(before)} to ${JSON.stringify(readPath(doc, key.path))}.`,
        proposedContent: JSON.stringify(doc, null, 2),
    };
};
